// =============================================================================
// useUniquePagesBulk — bulk generacja podstron (worker pool + pause/stop/progress).
// Wzorzec z useSchemaBackfill.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import {
  runUniquePagesBulk,
  type UniqueBulkItem,
  type UniqueBulkControl,
  type UniqueBulkProgress,
  type UniqueBulkSummary,
} from "@/services/uniquePages/uniquePagesBulkRunner";

const INITIAL: UniqueBulkProgress = {
  current: 0, total: 0, ok: 0, skipped: 0, failed: 0, activeLabels: [], phase: "running",
};

export function useUniquePagesBulk() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<UniqueBulkProgress>(INITIAL);
  const [summary, setSummary] = useState<UniqueBulkSummary | null>(null);
  const pauseRef = useRef(false);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!running) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Trwa generowanie podstron — zamknąć kartę?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [running]);

  const start = useCallback(async (items: UniqueBulkItem[], concurrency = 2): Promise<void> => {
    if (!items.length || running) return;
    setRunning(true);
    pauseRef.current = false;
    stopRef.current = false;
    setSummary(null);
    setProgress({ ...INITIAL, total: items.length });
    const control: UniqueBulkControl = {
      isPaused: () => pauseRef.current,
      isStopRequested: () => stopRef.current,
    };
    try {
      const result = await runUniquePagesBulk(items, control, setProgress, Date.now(), concurrency);
      setSummary(result);
    } finally {
      setRunning(false);
      pauseRef.current = false;
      stopRef.current = false;
    }
  }, [running]);

  const pause = useCallback(() => { if (running) { pauseRef.current = true; setProgress((p) => ({ ...p, phase: "paused" })); } }, [running]);
  const resume = useCallback(() => { if (running) { pauseRef.current = false; setProgress((p) => ({ ...p, phase: "running" })); } }, [running]);
  const stop = useCallback(() => { if (running) { stopRef.current = true; setProgress((p) => ({ ...p, phase: "stopping" })); } }, [running]);

  return { running, progress, summary, start, pause, resume, stop };
}
